// Phone-prefix → IANA timezone defaults for the recipient form.
// Used to pre-fill the timezone dropdown so US/Canada/Pakistan/etc. users
// don't have to know IANA names. The user can always override.
//
// Coverage strategy:
//   - Unique country codes map directly (e.g. +92 → Asia/Karachi).
//   - NANP (+1) splits by area code for Canadian provinces and US time zones.
//   - Unknown prefixes return null — caller falls back to browser timezone.
//
// This file is data-only; no side effects, no app coupling. Safe to delete or
// extend without touching anything else.

export interface TimezoneOption {
  value: string;
  label: string;
  region: string;
}

// Curated dropdown list — ~70 entries grouped by region. Not exhaustive;
// edge-case zones can be typed in via the "Other" path if we ever expose one.
export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  // Americas — North
  { value: 'America/Los_Angeles', label: 'Los Angeles (PT)', region: 'Americas' },
  { value: 'America/Vancouver', label: 'Vancouver (PT)', region: 'Americas' },
  { value: 'America/Denver', label: 'Denver (MT)', region: 'Americas' },
  { value: 'America/Phoenix', label: 'Phoenix (MST, no DST)', region: 'Americas' },
  { value: 'America/Edmonton', label: 'Edmonton / Calgary (MT)', region: 'Americas' },
  { value: 'America/Chicago', label: 'Chicago (CT)', region: 'Americas' },
  { value: 'America/Mexico_City', label: 'Mexico City (CT)', region: 'Americas' },
  { value: 'America/Winnipeg', label: 'Winnipeg (CT)', region: 'Americas' },
  { value: 'America/New_York', label: 'New York (ET)', region: 'Americas' },
  { value: 'America/Toronto', label: 'Toronto / Ottawa (ET)', region: 'Americas' },
  { value: 'America/Montreal', label: 'Montreal (ET)', region: 'Americas' },
  { value: 'America/Halifax', label: 'Halifax (AT)', region: 'Americas' },
  { value: 'America/St_Johns', label: "St. John's (NT)", region: 'Americas' },
  { value: 'America/Anchorage', label: 'Anchorage (AKT)', region: 'Americas' },
  { value: 'Pacific/Honolulu', label: 'Honolulu (HST)', region: 'Americas' },
  // Americas — Caribbean
  { value: 'America/Nassau', label: 'Nassau (Bahamas)', region: 'Caribbean' },
  { value: 'America/Jamaica', label: 'Jamaica', region: 'Caribbean' },
  { value: 'America/Port-au-Prince', label: 'Haiti', region: 'Caribbean' },
  { value: 'America/Santo_Domingo', label: 'Dominican Republic', region: 'Caribbean' },
  { value: 'America/Port_of_Spain', label: 'Trinidad & Tobago', region: 'Caribbean' },
  { value: 'America/Barbados', label: 'Barbados', region: 'Caribbean' },
  { value: 'America/Puerto_Rico', label: 'Puerto Rico', region: 'Caribbean' },
  // Americas — South
  { value: 'America/Sao_Paulo', label: 'São Paulo', region: 'Americas (South)' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires', region: 'Americas (South)' },
  { value: 'America/Bogota', label: 'Bogotá', region: 'Americas (South)' },
  { value: 'America/Lima', label: 'Lima', region: 'Americas (South)' },
  { value: 'America/Santiago', label: 'Santiago', region: 'Americas (South)' },
  // Europe
  { value: 'Europe/London', label: 'London (GMT/BST)', region: 'Europe' },
  { value: 'Europe/Dublin', label: 'Dublin', region: 'Europe' },
  { value: 'Europe/Lisbon', label: 'Lisbon', region: 'Europe' },
  { value: 'Europe/Madrid', label: 'Madrid', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris', region: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin', region: 'Europe' },
  { value: 'Europe/Rome', label: 'Rome', region: 'Europe' },
  { value: 'Europe/Zurich', label: 'Zürich', region: 'Europe' },
  { value: 'Europe/Stockholm', label: 'Stockholm', region: 'Europe' },
  { value: 'Europe/Warsaw', label: 'Warsaw', region: 'Europe' },
  { value: 'Europe/Athens', label: 'Athens', region: 'Europe' },
  { value: 'Europe/Istanbul', label: 'Istanbul', region: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow', region: 'Europe' },
  // Africa
  { value: 'Africa/Lagos', label: 'Lagos', region: 'Africa' },
  { value: 'Africa/Cairo', label: 'Cairo', region: 'Africa' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg', region: 'Africa' },
  { value: 'Africa/Nairobi', label: 'Nairobi', region: 'Africa' },
  { value: 'Africa/Casablanca', label: 'Casablanca', region: 'Africa' },
  // Middle East
  { value: 'Asia/Dubai', label: 'Dubai', region: 'Middle East' },
  { value: 'Asia/Riyadh', label: 'Riyadh', region: 'Middle East' },
  { value: 'Asia/Qatar', label: 'Doha', region: 'Middle East' },
  { value: 'Asia/Jerusalem', label: 'Jerusalem', region: 'Middle East' },
  { value: 'Asia/Tehran', label: 'Tehran', region: 'Middle East' },
  // South Asia
  { value: 'Asia/Karachi', label: 'Karachi / Pakistan', region: 'South Asia' },
  { value: 'Asia/Kolkata', label: 'India (IST)', region: 'South Asia' },
  { value: 'Asia/Dhaka', label: 'Dhaka', region: 'South Asia' },
  { value: 'Asia/Colombo', label: 'Colombo', region: 'South Asia' },
  { value: 'Asia/Kathmandu', label: 'Kathmandu', region: 'South Asia' },
  // East / SE Asia
  { value: 'Asia/Bangkok', label: 'Bangkok', region: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore', region: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong', region: 'Asia' },
  { value: 'Asia/Shanghai', label: 'Shanghai (China)', region: 'Asia' },
  { value: 'Asia/Taipei', label: 'Taipei', region: 'Asia' },
  { value: 'Asia/Seoul', label: 'Seoul', region: 'Asia' },
  { value: 'Asia/Tokyo', label: 'Tokyo', region: 'Asia' },
  { value: 'Asia/Manila', label: 'Manila', region: 'Asia' },
  { value: 'Asia/Jakarta', label: 'Jakarta', region: 'Asia' },
  { value: 'Asia/Kuala_Lumpur', label: 'Kuala Lumpur', region: 'Asia' },
  // Oceania
  { value: 'Australia/Sydney', label: 'Sydney', region: 'Oceania' },
  { value: 'Australia/Melbourne', label: 'Melbourne', region: 'Oceania' },
  { value: 'Australia/Brisbane', label: 'Brisbane', region: 'Oceania' },
  { value: 'Australia/Perth', label: 'Perth', region: 'Oceania' },
  { value: 'Pacific/Auckland', label: 'Auckland', region: 'Oceania' },
  { value: 'Pacific/Fiji', label: 'Fiji', region: 'Oceania' },
  // Fallback
  { value: 'UTC', label: 'UTC', region: 'Other' },
];

// Unique country codes → default IANA. NANP (+1) is handled separately by
// area-code lookup below.
const COUNTRY_TZ: Record<string, string> = {
  // Europe
  '44': 'Europe/London',
  '353': 'Europe/Dublin',
  '351': 'Europe/Lisbon',
  '34': 'Europe/Madrid',
  '33': 'Europe/Paris',
  '32': 'Europe/Brussels',
  '31': 'Europe/Amsterdam',
  '49': 'Europe/Berlin',
  '43': 'Europe/Vienna',
  '41': 'Europe/Zurich',
  '39': 'Europe/Rome',
  '48': 'Europe/Warsaw',
  '420': 'Europe/Prague',
  '36': 'Europe/Budapest',
  '40': 'Europe/Bucharest',
  '46': 'Europe/Stockholm',
  '47': 'Europe/Oslo',
  '45': 'Europe/Copenhagen',
  '358': 'Europe/Helsinki',
  '30': 'Europe/Athens',
  '90': 'Europe/Istanbul',
  '7': 'Europe/Moscow',
  '380': 'Europe/Kyiv',
  // Africa
  '20': 'Africa/Cairo',
  '212': 'Africa/Casablanca',
  '213': 'Africa/Algiers',
  '216': 'Africa/Tunis',
  '234': 'Africa/Lagos',
  '254': 'Africa/Nairobi',
  '27': 'Africa/Johannesburg',
  // Middle East
  '971': 'Asia/Dubai',
  '966': 'Asia/Riyadh',
  '974': 'Asia/Qatar',
  '973': 'Asia/Bahrain',
  '965': 'Asia/Kuwait',
  '968': 'Asia/Muscat',
  '972': 'Asia/Jerusalem',
  '98': 'Asia/Tehran',
  '964': 'Asia/Baghdad',
  '961': 'Asia/Beirut',
  '962': 'Asia/Amman',
  // South Asia
  '92': 'Asia/Karachi',
  '91': 'Asia/Kolkata',
  '880': 'Asia/Dhaka',
  '94': 'Asia/Colombo',
  '977': 'Asia/Kathmandu',
  // East / SE Asia
  '66': 'Asia/Bangkok',
  '65': 'Asia/Singapore',
  '852': 'Asia/Hong_Kong',
  '853': 'Asia/Macau',
  '86': 'Asia/Shanghai',
  '886': 'Asia/Taipei',
  '82': 'Asia/Seoul',
  '81': 'Asia/Tokyo',
  '63': 'Asia/Manila',
  '62': 'Asia/Jakarta',
  '60': 'Asia/Kuala_Lumpur',
  '84': 'Asia/Ho_Chi_Minh',
  // Oceania
  '61': 'Australia/Sydney',
  '64': 'Pacific/Auckland',
  '679': 'Pacific/Fiji',
  // Latin America (non-NANP)
  '52': 'America/Mexico_City',
  '55': 'America/Sao_Paulo',
  '54': 'America/Argentina/Buenos_Aires',
  '56': 'America/Santiago',
  '57': 'America/Bogota',
  '58': 'America/Caracas',
  '51': 'America/Lima',
  '593': 'America/Guayaquil',
};

// NANP (+1) area codes that aren't on US Eastern. Anything not in this map
// falls back to America/New_York (covers most of the US East Coast + the
// majority of NANP traffic in the platform's target markets).
const NANP_AREA_TZ: Record<string, string> = {
  // — Canada —
  // Ontario (Eastern)
  '416': 'America/Toronto', '647': 'America/Toronto', '437': 'America/Toronto',
  '905': 'America/Toronto', '289': 'America/Toronto', '365': 'America/Toronto',
  '613': 'America/Toronto', '343': 'America/Toronto', '519': 'America/Toronto',
  '226': 'America/Toronto', '548': 'America/Toronto', '705': 'America/Toronto',
  '249': 'America/Toronto', '807': 'America/Toronto',
  // Quebec (Eastern)
  '514': 'America/Montreal', '438': 'America/Montreal', '450': 'America/Montreal',
  '579': 'America/Montreal', '418': 'America/Montreal', '581': 'America/Montreal',
  '367': 'America/Montreal', '873': 'America/Montreal', '819': 'America/Montreal',
  '873-2': 'America/Montreal',
  // Atlantic provinces
  '902': 'America/Halifax', '782': 'America/Halifax',  // NS, PEI
  '506': 'America/Moncton',                              // NB
  '709': 'America/St_Johns', '879': 'America/St_Johns', // NL
  // Manitoba / Saskatchewan (Central)
  '204': 'America/Winnipeg', '431': 'America/Winnipeg', '584': 'America/Winnipeg',
  '306': 'America/Regina', '639': 'America/Regina', '474': 'America/Regina',
  // Alberta (Mountain)
  '403': 'America/Edmonton', '587': 'America/Edmonton', '780': 'America/Edmonton',
  '825': 'America/Edmonton', '368': 'America/Edmonton',
  // BC (Pacific)
  '604': 'America/Vancouver', '778': 'America/Vancouver', '236': 'America/Vancouver',
  '672': 'America/Vancouver', '250': 'America/Vancouver',
  // Yukon / NWT / Nunavut
  '867': 'America/Whitehorse',

  // — US Pacific —
  '206': 'America/Los_Angeles', '253': 'America/Los_Angeles', '360': 'America/Los_Angeles',
  '425': 'America/Los_Angeles', '509': 'America/Los_Angeles', '564': 'America/Los_Angeles',
  '503': 'America/Los_Angeles', '541': 'America/Los_Angeles', '458': 'America/Los_Angeles',
  '971': 'America/Los_Angeles',
  '213': 'America/Los_Angeles', '310': 'America/Los_Angeles', '323': 'America/Los_Angeles',
  '408': 'America/Los_Angeles', '415': 'America/Los_Angeles', '510': 'America/Los_Angeles',
  '530': 'America/Los_Angeles', '559': 'America/Los_Angeles', '562': 'America/Los_Angeles',
  '619': 'America/Los_Angeles', '626': 'America/Los_Angeles', '650': 'America/Los_Angeles',
  '657': 'America/Los_Angeles', '661': 'America/Los_Angeles', '669': 'America/Los_Angeles',
  '707': 'America/Los_Angeles', '714': 'America/Los_Angeles', '747': 'America/Los_Angeles',
  '760': 'America/Los_Angeles', '805': 'America/Los_Angeles', '818': 'America/Los_Angeles',
  '831': 'America/Los_Angeles', '858': 'America/Los_Angeles', '909': 'America/Los_Angeles',
  '916': 'America/Los_Angeles', '925': 'America/Los_Angeles', '949': 'America/Los_Angeles',
  '951': 'America/Los_Angeles',
  '702': 'America/Los_Angeles', '725': 'America/Los_Angeles', '775': 'America/Los_Angeles',

  // — US Mountain —
  '208': 'America/Boise',   // most of Idaho (MT/PT split — default MT)
  '303': 'America/Denver', '720': 'America/Denver', '970': 'America/Denver',
  '719': 'America/Denver',
  '307': 'America/Denver',  // Wyoming
  '385': 'America/Denver', '435': 'America/Denver', '801': 'America/Denver',  // Utah
  '406': 'America/Denver',  // Montana
  '505': 'America/Denver', '575': 'America/Denver',  // New Mexico
  '480': 'America/Phoenix', '602': 'America/Phoenix', '623': 'America/Phoenix',  // AZ (no DST)
  '520': 'America/Phoenix', '928': 'America/Phoenix',

  // — US Central —
  '205': 'America/Chicago', '251': 'America/Chicago', '256': 'America/Chicago', '334': 'America/Chicago',  // AL
  '479': 'America/Chicago', '501': 'America/Chicago', '870': 'America/Chicago',  // AR
  '224': 'America/Chicago', '309': 'America/Chicago', '312': 'America/Chicago', '331': 'America/Chicago',
  '618': 'America/Chicago', '630': 'America/Chicago', '708': 'America/Chicago', '773': 'America/Chicago',
  '779': 'America/Chicago', '815': 'America/Chicago', '847': 'America/Chicago', '872': 'America/Chicago',  // IL
  '217': 'America/Chicago',
  '219': 'America/Chicago', '574': 'America/Chicago', '812': 'America/Chicago',  // IN (parts; 812 covers Evansville Central)
  '319': 'America/Chicago', '515': 'America/Chicago', '563': 'America/Chicago', '641': 'America/Chicago', '712': 'America/Chicago',  // IA
  '316': 'America/Chicago', '620': 'America/Chicago', '785': 'America/Chicago', '913': 'America/Chicago',  // KS
  '270': 'America/Chicago', '364': 'America/Chicago',  // KY (west)
  '225': 'America/Chicago', '318': 'America/Chicago', '337': 'America/Chicago', '504': 'America/Chicago', '985': 'America/Chicago',  // LA
  '218': 'America/Chicago', '320': 'America/Chicago', '507': 'America/Chicago', '612': 'America/Chicago', '651': 'America/Chicago', '763': 'America/Chicago', '952': 'America/Chicago',  // MN
  '228': 'America/Chicago', '601': 'America/Chicago', '662': 'America/Chicago', '769': 'America/Chicago',  // MS
  '314': 'America/Chicago', '417': 'America/Chicago', '573': 'America/Chicago', '636': 'America/Chicago', '660': 'America/Chicago', '816': 'America/Chicago', '975': 'America/Chicago',  // MO
  '308': 'America/Chicago', '402': 'America/Chicago', '531': 'America/Chicago',  // NE
  '701': 'America/Chicago',  // ND
  '405': 'America/Chicago', '539': 'America/Chicago', '580': 'America/Chicago', '918': 'America/Chicago',  // OK
  '605': 'America/Chicago',  // SD
  '423': 'America/Chicago', '615': 'America/Chicago', '629': 'America/Chicago', '731': 'America/Chicago', '865': 'America/Chicago', '901': 'America/Chicago', '931': 'America/Chicago',  // TN
  '210': 'America/Chicago', '214': 'America/Chicago', '254': 'America/Chicago', '281': 'America/Chicago',
  '361': 'America/Chicago', '409': 'America/Chicago', '430': 'America/Chicago', '432': 'America/Chicago',
  '469': 'America/Chicago', '512': 'America/Chicago', '682': 'America/Chicago', '713': 'America/Chicago',
  '737': 'America/Chicago', '806': 'America/Chicago', '817': 'America/Chicago', '830': 'America/Chicago',
  '832': 'America/Chicago', '903': 'America/Chicago', '915': 'America/Chicago', '936': 'America/Chicago',
  '940': 'America/Chicago', '945': 'America/Chicago', '956': 'America/Chicago', '972': 'America/Chicago',
  '979': 'America/Chicago',  // TX
  '262': 'America/Chicago', '274': 'America/Chicago', '414': 'America/Chicago', '534': 'America/Chicago',
  '608': 'America/Chicago', '715': 'America/Chicago', '920': 'America/Chicago',  // WI

  // — US Alaska / Hawaii —
  '907': 'America/Anchorage',
  '808': 'Pacific/Honolulu',

  // — Caribbean (NANP) —
  '242': 'America/Nassau',          // Bahamas
  '246': 'America/Barbados',        // Barbados
  '264': 'America/Anguilla',        // Anguilla
  '268': 'America/Antigua',         // Antigua
  '284': 'America/Tortola',         // BVI
  '345': 'America/Cayman',          // Cayman Islands
  '441': 'Atlantic/Bermuda',        // Bermuda
  '473': 'America/Grenada',         // Grenada
  '649': 'America/Grand_Turk',      // Turks & Caicos
  '658': 'America/Jamaica',         // Jamaica
  '664': 'America/Montserrat',      // Montserrat
  '670': 'Pacific/Saipan',          // Northern Mariana Islands
  '671': 'Pacific/Guam',            // Guam
  '684': 'Pacific/Pago_Pago',       // American Samoa
  '721': 'America/Curacao',         // Sint Maarten
  '758': 'America/St_Lucia',        // Saint Lucia
  '767': 'America/Dominica',        // Dominica
  '784': 'America/St_Vincent',      // St. Vincent & Grenadines
  '787': 'America/Puerto_Rico',     // Puerto Rico
  '809': 'America/Santo_Domingo',   // DR
  '829': 'America/Santo_Domingo',
  '849': 'America/Santo_Domingo',
  '868': 'America/Port_of_Spain',   // Trinidad & Tobago
  '869': 'America/St_Kitts',        // St. Kitts & Nevis
  '876': 'America/Jamaica',         // Jamaica
  '939': 'America/Puerto_Rico',     // Puerto Rico
};

// Strip leading +, spaces, dashes, parens. Returns just digits.
function digits(phone: string): string {
  return (phone || '').replace(/[^0-9]/g, '');
}

/** Returns a best-guess IANA timezone for an E.164 phone number, or null if
 *  no confident match. Caller decides what to do with null (typically: keep
 *  whatever timezone is already selected). */
export function phoneToTimezone(phone: string): string | null {
  const d = digits(phone);
  if (d.length < 4) return null;

  // NANP: country code is '1', followed by 3-digit area code.
  if (d.startsWith('1') && d.length >= 4) {
    const area = d.slice(1, 4);
    if (NANP_AREA_TZ[area]) return NANP_AREA_TZ[area];
    // Default unknown NANP (mostly US Eastern + any unmapped Caribbean) to ET.
    return 'America/New_York';
  }

  // Try longest country-code prefix match (3 → 2 → 1 digit).
  for (const len of [3, 2, 1]) {
    if (d.length < len) continue;
    const prefix = d.slice(0, len);
    if (COUNTRY_TZ[prefix]) return COUNTRY_TZ[prefix];
  }

  return null;
}
